/**
 * registroTecnico.js — il registro tecnico del browser per le segnalazioni
 * degli errori (migration 346, proposta del 28/10/2026).
 *
 * UN ANELLO DI DIMENSIONE FISSA, IN MEMORIA. Scrivere una voce e'
 * un'assegnazione in un array di 300 posti: niente si serializza, niente va
 * in rete, niente finisce in localStorage finche' qualcuno non preme
 * «Segnala un problema». Alla ricarica il registro riparte da zero, e su una
 * postazione condivisa non resta niente sul disco.
 *
 * COSA SI SCRIVE, e cosa no (le regole stanno anche nel server, che non si
 * fida di questo file: backend/services/segnalazioni.js):
 *   - navigazioni: il solo percorso, senza query ne' frammento;
 *   - chiamate API: metodo, percorso senza query, stato, durata in ms. Mai i
 *     corpi, mai le intestazioni (ci sono gettoni e chiavi). Le annota
 *     services/api.js nei SUOI intercettori di sempre, quelli che contano le
 *     richieste in volo: nessun secondo intercettore;
 *   - errori della pagina (window 'error' e 'unhandledrejection'): il
 *     messaggio SOLO se ha una forma conosciuta (elenco chiuso in
 *     messaggiErroreConosciuti.json: i nomi degli errori di JavaScript e i
 *     messaggi fissi del browser, di axios e del nostro codice), altrimenti
 *     «messaggio omesso (<tipo>, <n> caratteri)»; poi tagliato a 200
 *     caratteri e senza la forma di un codice fiscale, di una partita IVA o
 *     di una posta; la pila coi soli nomi dei file. La console NON si
 *     intercetta: si raccolgono gli errori, non i log.
 *     Perche' un elenco chiuso e non un filtro delle forme (revisione del
 *     29/10, punto 4): un errore lanciato dal nostro codice o da una libreria
 *     puo' portare nel messaggio un nome, un testo scritto, un pezzo di una
 *     risposta («Unexpected token 'M', "Mario..." is not valid JSON»), e un
 *     cognome non ha una forma da riconoscere. Un elenco di cose vietate
 *     dimentica sempre qualcosa (CLAUDE.md, il tipo dei file caricati).
 * Delle voci restano le ultime 300 o gli ultimi 10 minuti, la prima delle due.
 */
import CONOSCIUTI from './messaggiErroreConosciuti.json';

const MAX = 300;
const FINESTRA_MS = 10 * 60 * 1000;
const MAX_MESSAGGIO = 200;

const anello = new Array(MAX);
let posto = 0;
let piene = 0;

/* Le stesse forme di `ripulisci` degli allarmi del server. */
const FORMA_CF = /[A-Z]{6}[0-9LMNPQRSTUV]{2}[ABCDEHLMPRST][0-9LMNPQRSTUV]{2}[A-Z][0-9LMNPQRSTUV]{3}[A-Z]/gi;
const FORMA_PIVA = /\b\d{11}\b/g;
const FORMA_EMAIL = /[\w.+-]+@[\w-]+\.[\w.]+/g;

export function ripulisci(testo, massimo = MAX_MESSAGGIO) {
    return String(testo === null || testo === undefined ? '' : testo)
        .replace(/[\u0000-\u001f\u007f]/g, ' ').replace(/\s+/g, ' ').trim()
        .slice(0, massimo)
        .replace(FORMA_CF, '[tolto]').replace(FORMA_EMAIL, '[tolto]').replace(FORMA_PIVA, '[tolto]');
}

/** Il percorso, senza la query, senza il frammento e senza l'origine. */
export function senzaQuery(indirizzo) {
    const s = String(indirizzo || '').split('#')[0].split('?')[0].replace(/^https?:\/\/[^/]+/i, '');
    return s.startsWith('/') ? s : `/${s}`;
}

function annota(voce) {
    anello[posto] = { t: Date.now(), ...voce };
    posto = (posto + 1) % MAX;
    if (piene < MAX) piene += 1;
}

export const annotaNavigazione = (percorso) => annota({ k: 'nav', p: senzaQuery(percorso) });

/** Da services/api.js: `partita` e' l'istante in cui la richiesta e' partita. */
export function annotaChiamata(metodo, url, stato, partita) {
    annota({
        k: 'api',
        m: String(metodo || 'get').toUpperCase(),
        p: senzaQuery(url),
        s: Number.isInteger(stato) ? stato : 0,
        ms: Number.isFinite(partita) ? Math.max(0, Date.now() - partita) : null,
    });
}

/** Una pila di chiamate coi soli nomi dei file e le righe. */
export function soloFile(pila) {
    const file = [...String(pila || '').matchAll(/([A-Za-z0-9_.-]+\.(?:js|jsx|mjs)):(\d+)(?::(\d+))?/g)]
        .slice(0, 8).map((m) => `${m[1]}:${m[2]}${m[3] ? `:${m[3]}` : ''}`);
    return file.length ? file.join(' ') : null;
}

const NOMI = new Set(CONOSCIUTI.nomi);
const FORME = CONOSCIUTI.forme.map((f) => new RegExp(f));
const OMESSO = /^messaggio omesso \(([A-Za-z]{1,40}), \d{1,7} caratteri\)$/;

/**
 * Il messaggio se ha una forma conosciuta, altrimenti «messaggio omesso
 * (<tipo>, <n> caratteri)». La stessa regola sta in
 * backend/services/segnalazioni.js (messaggioConosciuto), sulle stesse forme;
 * i casi del file JSON li fanno girare tutte e due le prove.
 */
export function messaggioConosciuto(grezzo, nomeErrore) {
    const s = String(grezzo === null || grezzo === undefined ? '' : grezzo)
        .replace(/[\u0000-\u001f\u007f]/g, ' ').replace(/\s+/g, ' ').trim();
    if (!s) return '';
    const o = OMESSO.exec(s);
    if (o && (NOMI.has(o[1]) || o[1] === 'sconosciuto')) return s;
    let resto = s.replace(/^Uncaught (?:\(in promise\) )?/, '');
    let nome = null;
    const m = /^([A-Za-z]{2,40})(?:: (.*))?$/.exec(resto);
    const sconosciuto = m && m[2] !== undefined && !NOMI.has(m[1]);
    if (m && NOMI.has(m[1])) { nome = m[1]; resto = m[2] === undefined ? '' : m[2]; }
    if (!sconosciuto) {
        if (nome && resto === '') return nome;
        const react = /^Minified React error #(\d{1,4})[;. ]/.exec(resto);
        if (react) resto = `Minified React error #${react[1]}`;
        if (FORME.some((f) => f.test(resto))) return nome ? `${nome}: ${resto}` : resto;
    }
    const tipo = nome || (NOMI.has(nomeErrore) ? nomeErrore : 'sconosciuto');
    return `messaggio omesso (${tipo}, ${s.length} caratteri)`;
}

export function annotaErrore(messaggio, pila, nomeErrore) {
    const msg = ripulisci(messaggioConosciuto(messaggio, nomeErrore));
    if (msg) annota({ k: 'err', msg, pila: soloFile(pila) });
}

/** Le voci, dalla piu' vecchia, degli ultimi 10 minuti. */
export function voci(adesso = Date.now()) {
    const out = [];
    for (let i = 0; i < piene; i++) {
        const v = anello[(posto - piene + i + MAX) % MAX];
        if (v && v.t >= adesso - FINESTRA_MS) out.push(v);
    }
    return out;
}

/** Per le prove: l'anello vuoto. */
export function svuota() {
    anello.fill(undefined);
    posto = 0;
    piene = 0;
}

let installato = false;
/** Gli ascoltatori degli errori, una volta sola per pagina. */
export function installaRegistro() {
    if (installato || typeof window === 'undefined') return;
    installato = true;
    window.addEventListener('error', (e) => {
        annotaErrore(e?.message || 'errore', e?.error?.stack || (e?.filename ? `${e.filename}:${e.lineno || 0}` : ''), e?.error?.name);
    });
    window.addEventListener('unhandledrejection', (e) => {
        const r = e?.reason;
        annotaErrore(r?.message || (typeof r === 'string' ? r : 'promessa rifiutata'), r?.stack || '', r?.name);
    });
}

export const LIMITI = { MAX, FINESTRA_MS, MAX_MESSAGGIO };
